import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  FileDownload as DownloadIcon,
  History as HistoryIcon,
  PostAdd as PrefillIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import { documentService } from '../services/documentService';
import { documentTemplateService } from '../services/documentTemplateService';
import PrefillDocumentModal from '../components/PrefillDocumentModal';
import RichTextEditor from '../components/RichTextEditor';
import RichTextViewer from '../components/RichTextViewer';
import DeleteDocumentButton from '../components/DeleteDocumentButton';
import { isOrdinaryDocument, toRichTextContent } from '../utils/richTextDocument';
import { useAuthStore } from '../stores/authStore';
import {
  IDocumentDetail,
  IDocumentRevision,
  IDocumentTemplate,
  DocumentContent,
  PdfDocumentBlock,
  QuestionnaireSectionContent,
  RichTextDocumentContent,
  WillDocumentBlock,
} from '../types/document';
import { stitchColors, stitchFonts } from '../theme/tokens';

export const extractTextFromTipTapJson = (node: any): string => {
  if (!node) return '';
  if (typeof node === 'string') return node;
  if (node.type === 'text') return node.text || '';
  if (node.type === 'hardBreak') return '\n';
  if (Array.isArray(node.content)) {
    const texts = node.content.map(extractTextFromTipTapJson);
    if (['doc', 'bulletList', 'orderedList', 'table'].includes(node.type)) {
      return texts.filter(Boolean).join('\n');
    }
    return texts.join('');
  }
  return '';
};

const DocumentViewer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const isLawyer = user?.userType === 'EL';

  const [document, setDocument] = useState<IDocumentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Revisions
  const [revisions, setRevisions] = useState<IDocumentRevision[]>([]);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [selectedRevision, setSelectedRevision] = useState<IDocumentRevision | null>(null);
  const [loadingRevision, setLoadingRevision] = useState(false);

  // Edit mode & library templates
  const [isEditing, setIsEditing] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editableContent, setEditableContent] = useState<DocumentContent | null>(null);
  const [conflictError, setConflictError] = useState('');
  const [libraryTemplates, setLibraryTemplates] = useState<IDocumentTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [prefillOpen, setPrefillOpen] = useState(false);

  const documentRef = useRef<HTMLDivElement>(null);

  const fetchDocument = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError('');
      const searchParams = new URLSearchParams(location.search);
      const versionQuery = searchParams.get('version');
      const versionParam = versionQuery ? Number(versionQuery) : undefined;
      const data = await documentService.getDocumentById(id, versionParam);
      setDocument(data);
      setEditTitle(data.title);
      if (isOrdinaryDocument(data.sourceType, data.currentRevision?.content)) {
        setEditableContent(toRichTextContent(data.currentRevision?.content, data.templateText));
      } else if (data.currentRevision?.content) {
        setEditableContent(JSON.parse(JSON.stringify(data.currentRevision.content)));
      } else if (data.templateText) {
        setEditableContent({
          kind: 'will',
          bodyText: data.templateText,
          blocks: [
            { id: 'b1', type: 'paragraph', text: data.templateText, order: 1 }
          ],
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to load document.');
    } finally {
      setLoading(false);
    }
  }, [id, location.search]);

  useEffect(() => {
    fetchDocument();
  }, [fetchDocument]);

  // Load library templates for lawyer formatting selection
  useEffect(() => {
    if (isLawyer && isEditing) {
      documentTemplateService.getDocumentTemplates().then(setLibraryTemplates).catch(console.error);
    }
  }, [isLawyer, isEditing]);

  // Download PDF export of the document view
  const handleDownloadPdf = useCallback(() => {
    if (!documentRef.current) return;
    const element = documentRef.current;
    const filename = `${(selectedRevision?.title || document?.title || 'document').replace(/[^a-zA-Z0-9_-]/g, '_')}.pdf`;
    const opt: any = {
      margin: 10,
      filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    };
    html2pdf().set(opt).from(element).save();
  }, [document?.title, selectedRevision?.title]);

  const canDownloadPdf = Boolean(document && document.permissions?.canDownloadPdf !== false);

  // Check URL params (edit, download)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('edit') === 'true' && document?.permissions.canEdit) {
      setIsEditing(true);
    }
    if (searchParams.get('download') === 'true' && document && !loading && canDownloadPdf) {
      const timer = setTimeout(() => {
        handleDownloadPdf();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [location.search, document?.permissions.canEdit, document, loading, handleDownloadPdf, canDownloadPdf]);

  // Fetch revisions list when opening drawer
  const handleOpenHistory = async () => {
    if (!id) return;
    setHistoryDrawerOpen(true);
    try {
      const revs = await documentService.getDocumentRevisions(id);
      setRevisions(revs);
    } catch (err) {
      console.error('Failed to load revisions', err);
    }
  };

  // View specific revision snapshot
  const handleSelectRevision = async (revId: string) => {
    if (!id) return;
    try {
      setLoadingRevision(true);
      const rev = await documentService.getDocumentRevisionById(id, revId);
      setSelectedRevision(rev);
      setHistoryDrawerOpen(false);
    } catch (err) {
      console.error('Failed to load revision details', err);
    } finally {
      setLoadingRevision(false);
    }
  };

  const handleReturnToCurrent = () => {
    setSelectedRevision(null);
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('version')) {
      searchParams.delete('version');
      const searchStr = searchParams.toString();
      navigate({
        pathname: location.pathname,
        search: searchStr ? `?${searchStr}` : '',
      }, { replace: true });
    }
  };

  // Save new revision edit
  const handleSaveEdit = async () => {
    if (!document || !editableContent) return;
    setSavingEdit(true);
    setConflictError('');
    setSuccessMessage('');

    try {
      await documentService.updateDocumentContent(document.id, {
        baseRevisionId: document.currentRevision?.id,
        contentHash: ordinaryDocument && !document.currentRevision?.id ? document.contentHash : undefined,
        title: editTitle.trim() || document.title,
        content: ordinaryDocument
          ? toRichTextContent(editableContent, document.templateText)
          : editableContent,
      });

      setSuccessMessage('New document revision saved successfully.');
      setIsEditing(false);
      // Clean query param
      navigate(`/document-library/documents/${document.id}`, { replace: true });
      await fetchDocument();
    } catch (err: any) {
      if (err.response?.status === 409) {
        setConflictError(
          'Conflict detected: This document has been modified by another user. Please reload the latest revision before saving.'
        );
      } else {
        setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save revision.');
      }
    } finally {
      setSavingEdit(false);
    }
  };

  // Content for display
  const ordinaryDocument = isOrdinaryDocument(document?.sourceType, document?.currentRevision?.content);
  const rawActiveContent: DocumentContent | undefined =
    selectedRevision?.content ||
    (isEditing ? editableContent || undefined : document?.currentRevision?.content);
  const activeContent = ordinaryDocument
    ? toRichTextContent(rawActiveContent, document?.templateText)
    : rawActiveContent;
  const extractionWarnings = document?.source?.warnings ||
    (rawActiveContent?.kind === 'pdf' ? rawActiveContent.warnings : []) || [];

  const searchParams = new URLSearchParams(location.search);
  const versionQuery = searchParams.get('version');
  const urlVersionNumber = versionQuery ? Number(versionQuery) : undefined;
  const isHistoricalSnapshot = Boolean(selectedRevision || urlVersionNumber);
  const activeRevisionNumber =
    selectedRevision?.revisionNumber ||
    urlVersionNumber ||
    document?.currentRevision?.revisionNumber ||
    1;
  const activeRevisionCreatedAt =
    selectedRevision?.createdAt || document?.currentRevision?.createdAt || document?.createdAt;

  // Handlers for modifying editableContent in edit mode
  const handleUpdatePdfBlockText = (blockIndex: number, text: string) => {
    if (!editableContent || editableContent.kind !== 'pdf') return;
    const nextBlocks = [...editableContent.blocks];
    nextBlocks[blockIndex] = { ...nextBlocks[blockIndex], text };
    setEditableContent({ ...editableContent, blocks: nextBlocks });
  };

  const handleAddPdfParagraph = () => {
    if (!editableContent || editableContent.kind !== 'pdf') return;
    const newBlock: PdfDocumentBlock = {
      id: `p_new_${Date.now()}`,
      type: 'paragraph',
      text: '',
      pageNumber: editableContent.pageCount || 1,
    };
    setEditableContent({
      ...editableContent,
      blocks: [...editableContent.blocks, newBlock],
    });
  };

  const handleUpdateQuestionnaireValue = (sectionIndex: number, fieldIndex: number, val: any) => {
    if (!editableContent || editableContent.kind !== 'questionnaire') return;
    const nextSections = [...editableContent.sections];
    const nextFields = [...nextSections[sectionIndex].fields];
    nextFields[fieldIndex] = {
      ...nextFields[fieldIndex],
      value: val,
      displayValue: typeof val === 'boolean' ? (val ? 'Yes' : 'No') : String(val),
    };
    nextSections[sectionIndex] = { ...nextSections[sectionIndex], fields: nextFields };
    setEditableContent({ ...editableContent, sections: nextSections });
  };

  const handleUpdateWillBodyText = (text: string) => {
    const blocks: WillDocumentBlock[] = text.split(/\n\n+/).filter(Boolean).map((t, idx) => ({
      id: `block_${idx + 1}`,
      type: 'paragraph',
      text: t.trim(),
      order: idx + 1,
    }));
    setEditableContent({
      kind: 'will',
      bodyText: text,
      blocks,
    });
  };

  const handleUpdateRichTextContent = (newContent: any, plainText?: string) => {
    const derivedText = plainText !== undefined
      ? plainText
      : typeof newContent === 'string'
        ? newContent
        : extractTextFromTipTapJson(newContent);
    setEditableContent({
      kind: 'rich_text',
      editorSchemaVersion: 1,
      editorContent: newContent,
      bodyText: derivedText,
    });
  };

  const handleApplyTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const tmpl = libraryTemplates.find((t) => t._id === templateId);
    if (!tmpl) return;
    if (tmpl.editorContent) {
      setEditableContent({
        kind: 'rich_text',
        editorSchemaVersion: tmpl.editorSchemaVersion || 1,
        editorContent: tmpl.editorContent,
        bodyText: tmpl.extractedText || tmpl.templateText || '',
      });
      return;
    }
    if (!tmpl.templateText) return;
    const text = tmpl.templateText;
    const blocks: WillDocumentBlock[] = text.split(/\n\n+/).filter(Boolean).map((t, idx) => ({
      id: `block_${idx + 1}`,
      type: 'paragraph',
      text: t.trim(),
      order: idx + 1,
    }));
    setEditableContent({
      kind: 'will',
      bodyText: text,
      blocks,
    });
  };

  if (loading || loadingRevision) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress sx={{ color: stitchColors.primary }} />
      </Box>
    );
  }

  if (error && !document) {
    return (
      <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="outlined" startIcon={<BackIcon />} onClick={() => navigate('/document-library')}>
          Back to Document History
        </Button>
      </Box>
    );
  }

  if (!document) return null;

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: { xs: 2, md: 3 }, fontFamily: stitchFonts.body }}>
      {/* Historical Revision Banner */}
      {isHistoricalSnapshot && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={handleReturnToCurrent}>
              Back to Latest Revision
            </Button>
          }
        >
          You are viewing a read-only snapshot of <strong>Revision #{activeRevisionNumber}</strong>
          {activeRevisionCreatedAt && !isNaN(new Date(activeRevisionCreatedAt).getTime())
            ? ` (created ${new Date(activeRevisionCreatedAt).toLocaleString()})`
            : ''}.
        </Alert>
      )}

      {/* Conflict Alert */}
      {conflictError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={fetchDocument}>
              Reload Latest
            </Button>
          }
        >
          {conflictError}
        </Alert>
      )}

      {/* Success Alert */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {/* Header and Controls */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: 3,
          pb: 2,
          borderBottom: `1px solid ${stitchColors.outlineVariant}`,
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          {isEditing ? (
            <TextField
              label="Document Title"
              fullWidth
              size="small"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              sx={{ maxWidth: 500 }}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {selectedRevision?.title || document.title}
              </Typography>
              <Chip
                label={`Rev ${activeRevisionNumber}`}
                color="primary"
                size="small"
              />
              {document.sourceType === 'uploaded_pdf' && (
                <Chip label="PDF Intake" color="secondary" size="small" variant="outlined" />
              )}
              {document.sourceType === 'questionnaire' && (
                <Chip label="Questionnaire Intake" color="primary" size="small" variant="outlined" />
              )}
              {document.finalized && (
                <Chip label="Finalized" color="success" size="small" />
              )}
            </Box>
          )}

          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
            Owner: {document.userEmail} &bull; Updated: {new Date(document.updatedAt || document.createdAt).toLocaleDateString()}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {document.permissions.canDelete && (
            <DeleteDocumentButton documentId={document.id} title={document.title} onDeleted={() => navigate('/document-library')} />
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<BackIcon />}
            onClick={() => navigate('/document-library')}
          >
            History
          </Button>

          <Button
            variant="outlined"
            size="small"
            startIcon={<HistoryIcon />}
            onClick={handleOpenHistory}
          >
            Revisions
          </Button>

          {isLawyer && !isHistoricalSnapshot && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<PrefillIcon />}
              onClick={() => setPrefillOpen(true)}
            >
              Pre-fill New File
            </Button>
          )}

          {!isHistoricalSnapshot && document.permissions.canEdit && !document.finalized && (
            isEditing ? (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  color="inherit"
                  disabled={savingEdit}
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  startIcon={savingEdit ? <CircularProgress size={16} /> : <SaveIcon />}
                  disabled={savingEdit}
                  onClick={handleSaveEdit}
                >
                  Save as Revision
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                size="small"
                color="secondary"
                startIcon={<EditIcon />}
                onClick={() => setIsEditing(true)}
              >
                Edit
              </Button>
            )
          )}

          {user?.userType === 'EX' && !document.finalized && (
            <Button
              variant="contained"
              size="small"
              color="secondary"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/assigned-documents/${document.id}`)}
            >
              Sign Document
            </Button>
          )}

          <Button
            variant="contained"
            size="small"
            color="primary"
            startIcon={<DownloadIcon />}
            disabled={!canDownloadPdf}
            onClick={handleDownloadPdf}
          >
            Export PDF
          </Button>
        </Box>
      </Box>

      {/* Main Document Content Canvas */}
      <Paper
        ref={documentRef}
        elevation={1}
        sx={{
          p: { xs: 3, md: 5 },
          borderRadius: 2,
          backgroundColor: '#FFFFFF',
          minHeight: 600,
          '@media print': {
            boxShadow: 'none',
            p: 0,
          },
        }}
      >
        {/* Render PDF Source Content */}
        {activeContent?.kind === 'pdf' && (
          <Box>
            {activeContent.warnings && activeContent.warnings.length > 0 && !isEditing && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <strong>Extraction provenance note:</strong> {activeContent.warnings.join('; ')}
              </Alert>
            )}

            {activeContent.blocks.map((block, idx) => {
              if (isEditing) {
                return (
                  <Box key={block.id || idx} sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Block #{idx + 1} ({block.type} &bull; Page {block.pageNumber})
                    </Typography>
                    <TextField
                      fullWidth
                      multiline={block.type === 'paragraph'}
                      rows={block.type === 'paragraph' ? 3 : 1}
                      size="small"
                      value={block.text || ''}
                      onChange={(e) => handleUpdatePdfBlockText(idx, e.target.value)}
                    />
                  </Box>
                );
              }

              if (block.type === 'heading') {
                return (
                  <Typography
                    key={block.id || idx}
                    variant={block.level === 1 ? 'h5' : 'h6'}
                    sx={{
                      fontWeight: 700,
                      mt: idx === 0 ? 0 : 3,
                      mb: 1.5,
                      color: 'text.primary',
                    }}
                  >
                    {block.text}
                  </Typography>
                );
              }

              if (block.type === 'table' && block.tableData) {
                return (
                  <Box key={block.id || idx} sx={{ my: 2 }}>
                    <Table size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
                      <TableHead sx={{ bgcolor: 'action.hover' }}>
                        <TableRow>
                          {block.tableData.headers.map((h, hIdx) => (
                            <TableCell key={hIdx} sx={{ fontWeight: 600 }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {block.tableData.rows.map((row, rIdx) => (
                          <TableRow key={rIdx}>
                            {row.map((cell, cIdx) => (
                              <TableCell key={cIdx}>{cell}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                );
              }

              return (
                <Box key={block.id || idx} sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.primary' }}>
                    {block.text}
                  </Typography>
                  {block.uncertain && (
                    <Chip label="Uncertain OCR" size="small" color="warning" variant="outlined" sx={{ mt: 0.5 }} />
                  )}
                </Box>
              );
            })}

            {isEditing && (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddPdfParagraph}>
                  Add Paragraph Block
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Render Questionnaire Source Content */}
        {activeContent?.kind === 'questionnaire' && (
          <Box>
            {activeContent.sections.map((section: QuestionnaireSectionContent, sIdx: number) => (
              <Box key={section.id || sIdx} sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
                  {section.title}
                </Typography>
                {section.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {section.description}
                  </Typography>
                )}
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {section.fields.map((field, fIdx) => (
                    <Box key={field.id || fIdx} sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {field.label}
                        </Typography>
                        {field.isStuck && (
                          <Chip label="Flagged for review" color="warning" size="small" variant="outlined" />
                        )}
                      </Box>

                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={field.value ?? ''}
                          onChange={(e) => handleUpdateQuestionnaireValue(sIdx, fIdx, e.target.value)}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ color: field.displayValue === 'Not answered' ? 'text.secondary' : 'text.primary', fontStyle: field.displayValue === 'Not answered' ? 'italic' : 'normal' }}>
                          {field.displayValue || 'Not answered'}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Render Rich Text Document Content */}
        {activeContent?.kind === 'rich_text' && (
          <Box>
            {ordinaryDocument && !isEditing && extractionWarnings.length > 0 && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <strong>Extraction provenance note:</strong> {extractionWarnings.join('; ')}
              </Alert>
            )}
            {isEditing ? (
              <Box>
                {isLawyer && libraryTemplates.length > 0 && (
                  <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                    <InputLabel id="template-library-select-label">Choose Document Library Format / Template</InputLabel>
                    <Select
                      labelId="template-library-select-label"
                      value={selectedTemplateId}
                      label="Choose Document Library Format / Template"
                      onChange={(e) => handleApplyTemplate(e.target.value)}
                    >
                      {libraryTemplates.map((t) => (
                        <MenuItem key={t._id} value={t._id}>
                          {t.title} (v{t.version || 1})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <RichTextEditor
                  content={ordinaryDocument ? activeContent.editorContent : (editableContent as RichTextDocumentContent)?.editorContent}
                  onChange={handleUpdateRichTextContent}
                  preserveLayout={ordinaryDocument}
                />
              </Box>
            ) : (
              ordinaryDocument ? (
                <RichTextEditor content={activeContent.editorContent} editable={false} preserveLayout />
              ) : (
                <RichTextViewer content={activeContent.editorContent || activeContent.bodyText} />
              )
            )}
          </Box>
        )}

        {/* Render Will Document Content */}
        {activeContent?.kind === 'will' && (
          <Box>
            {isEditing ? (
              <Box>
                {isLawyer && libraryTemplates.length > 0 && (
                  <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                    <InputLabel id="template-library-select-label">Choose Document Library Format / Template</InputLabel>
                    <Select
                      labelId="template-library-select-label"
                      value={selectedTemplateId}
                      label="Choose Document Library Format / Template"
                      onChange={(e) => handleApplyTemplate(e.target.value)}
                    >
                      {libraryTemplates.map((t) => (
                        <MenuItem key={t._id} value={t._id}>
                          {t.title} (v{t.version || 1})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                  Will Document Text
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={14}
                  maxRows={40}
                  value={activeContent.bodyText || ''}
                  onChange={(e) => handleUpdateWillBodyText(e.target.value)}
                  placeholder="Will text..."
                  helperText="Editing will create a new document revision and invalidate existing signatures."
                />
              </Box>
            ) : (
              <RichTextViewer content={activeContent.bodyText} />
            )}
          </Box>
        )}

        {/* Fallback Legacy Template Rendering */}
        {(!activeContent || activeContent.kind === 'legacy' || !['pdf', 'questionnaire', 'will', 'rich_text'].includes(activeContent?.kind as string)) && (
          <Box>
            {isEditing ? (
              <TextField
                fullWidth
                multiline
                minRows={14}
                maxRows={40}
                value={(editableContent as any)?.bodyText || document.templateText || ''}
                onChange={(e) => handleUpdateWillBodyText(e.target.value)}
              />
            ) : (
              <RichTextViewer content={document.templateText || 'No document text content available.'} />
            )}
          </Box>
        )}
      </Paper>

      {/* Revision History Drawer */}
      <Drawer
        anchor="right"
        open={historyDrawerOpen}
        onClose={() => setHistoryDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: 300, sm: 380 }, p: 3 } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Revision History
          </Typography>
          <IconButton onClick={() => setHistoryDrawerOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {revisions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No revision history found.
          </Typography>
        ) : (
          <List>
            {revisions.map((rev) => (
              <ListItem key={rev.id} disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => handleSelectRevision(rev.id)}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Rev #{rev.revisionNumber}
                        </Typography>
                        <Chip
                          label={rev.changeOrigin.replace(/_/g, ' ')}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        By {rev.createdByUserType} &bull; {new Date(rev.createdAt).toLocaleString()}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Drawer>

      {isLawyer && (
        <PrefillDocumentModal
          open={prefillOpen}
          sourceDocumentId={document.id}
          sourceDocumentTitle={document.title}
          onClose={() => setPrefillOpen(false)}
          onCreated={(created) => navigate(`/prefilled-documents/${created.id}`)}
        />
      )}
    </Box>
  );
};

export default DocumentViewer;
